<template>
  <AdminLayout :breadcrumb="`${folder.name || 'Folder'} Details`" @logout="logout">
    <div class="space-y-6">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <!-- Content -->
      <div v-else class="space-y-6">
        <!-- Back Button & Header with Breadcrumb -->
        <div class="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          <button
            @click="goBackToPreviousFolder"
            class="self-start p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
          </button>
          <div class="flex-1 min-w-0">
            <!-- Breadcrumb Navigation -->
            <nav class="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-500 mb-2 overflow-x-auto" v-if="folder.hierarchy && folder.hierarchy.length > 1">
              <router-link to="/admin/dashboard" class="hover:text-red-600 transition-colors font-medium whitespace-nowrap">Dashboard</router-link>
              <svg class="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
              <template v-for="(item, index) in folder.hierarchy">
                <button 
                  v-if="index < folder.hierarchy.length - 1"
                  @click="navigateToFolder(item.id)"
                  class="hover:text-red-600 transition-colors cursor-pointer font-medium whitespace-nowrap"
                >
                  {{ item.name }}
                </button>
                <span v-else class="text-gray-900 font-semibold whitespace-nowrap">{{ item.name }}</span>
                <svg v-if="index < folder.hierarchy.length - 1" class="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </template>
            </nav>
            <div>
              <h1 class="text-xl sm:text-2xl font-bold text-gray-900 break-words">{{ folder.name }}</h1>
              <p class="text-sm sm:text-base text-gray-600 break-words">{{ folder.description || 'No description provided' }}</p>
              
              <!-- Show inherited access indicator for non-owners -->
              <div v-if="folder.parent_id && !canManageAccess" class="mt-2">
                <span class="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-700">
                  <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                  </svg>
                  Access inherited from parent folder
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Folder Stats -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div class="flex items-center">
              <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-600">Total Files</p>
                <p class="text-2xl font-bold text-gray-900">{{ folder.stats?.total_files || 0 }}</p>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div class="flex items-center">
              <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-600">Storage Used</p>
                <p class="text-2xl font-bold text-gray-900">{{ formatFileSize(folder.stats?.total_size || 0) }}</p>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div class="flex items-center">
              <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-600">Shared With</p>
                <p class="text-2xl font-bold text-gray-900">{{ folder.users?.length || 0 }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- File Upload Area -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900">Files</h3>
            <div class="flex space-x-3">
              <button
                v-if="canCreateSubfolder"
                @click="showCreateSubfolderModal = true"
                class="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white px-4 py-3 rounded-xl text-sm font-semibold flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
              >
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
                </svg>
                <span>Create Subfolder</span>
              </button>
              <button
                v-if="canWriteFiles"
                @click="triggerFileUpload"
                class="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl text-sm font-semibold flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
              >
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                </svg>
                <span>Upload Files</span>
              </button>
            </div>
          </div>

          <!-- Drag and Drop Area -->
          <div
            v-if="canWriteFiles"
            @drop="handleDrop"
            @dragover.prevent
            @dragenter.prevent
            class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors"
            :class="{ 'border-blue-400 bg-blue-50': dragOver }"
            @dragenter="dragOver = true"
            @dragleave="dragOver = false"
          >
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
            </svg>
            <p class="mt-2 text-sm text-gray-600">
              <span class="font-medium">Click to upload</span> or drag and drop files here
            </p>
            <p class="text-xs text-gray-500">Support for multiple files up to 50MB each</p>
          </div>
          
          <!-- Read-only access message -->
          <div v-else-if="folder.userAccessLevel === 'read'" class="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center bg-gray-50">
            <svg class="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
            </svg>
            <p class="mt-2 text-sm text-gray-500">
              You have <span class="font-medium">read-only access</span> to this folder
            </p>
            <p class="text-xs text-gray-400">Contact the folder owner to request write access</p>
          </div>

          <!-- Hidden File Input -->
          <input
            ref="fileInput"
            type="file"
            multiple
            @change="handleFileSelect"
            class="hidden"
          />

          <!-- Files List -->
          <div v-if="folder.files && folder.files.length > 0" class="mt-8">
            <div class="flex flex-col sm:flex-row sm:items-center justify-between mb-6 space-y-4 sm:space-y-0">
              <!-- <h4 class="text-lg font-semibold text-gray-900">Uploaded Files</h4> -->
              
              <!-- Search and Filter Controls -->
              <div class="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 min-w-0 flex-1 sm:flex-initial sm:max-w-md">
                <!-- Search Input -->
                <div class="relative flex-1 sm:min-w-0">
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search files..."
                    class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  />
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                  </div>
                  <!-- Clear search button -->
                  <button
                    v-if="searchQuery"
                    @click="searchQuery = ''"
                    class="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg class="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
                
                <!-- File Type Filter -->
                <div class="relative">
                  <select
                    v-model="selectedFileType"
                    class="w-full sm:w-auto pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm bg-white"
                  >
                    <option value="">All Types</option>
                    <option value="image">Images</option>
                    <option value="document">Documents</option>
                    <option value="spreadsheet">Spreadsheets</option>
                    <option value="pdf">PDFs</option>
                    <option value="archive">Archives</option>
                    <option value="video">Videos</option>
                    <option value="audio">Audio</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <!-- Results Count -->
              <span class="text-sm text-gray-500 whitespace-nowrap">
                {{ filteredFiles.length }} of {{ folder.files.length }} file{{ folder.files.length !== 1 ? 's' : '' }}
                <span v-if="searchQuery || selectedFileType" class="text-red-600">filtered</span>
              </span>
            </div>

            <!-- No Results Message -->
            <div v-if="filteredFiles.length === 0" class="text-center py-8">
              <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <h3 class="mt-2 text-sm font-medium text-gray-900">No files found</h3>
              <p class="mt-1 text-sm text-gray-500">
                <span v-if="searchQuery && selectedFileType">
                  No {{ selectedFileType }} files match "{{ searchQuery }}"
                </span>
                <span v-else-if="searchQuery">
                  No files match "{{ searchQuery }}"
                </span>
                <span v-else-if="selectedFileType">
                  No {{ selectedFileType }} files found
                </span>
                <span v-else>Try adjusting your search or filter</span>
              </p>
              <div class="mt-4">
                <button
                  @click="clearFilters"
                  class="text-red-600 hover:text-red-500 text-sm font-medium"
                >
                  Clear filters
                </button>
              </div>
            </div>
            
            <div v-else class="grid grid-cols-1 gap-4">
                            <div 
                v-for="file in filteredFiles" 
                :key="file.id"
                class="group bg-white border border-gray-200 rounded-xl p-4 sm:p-6 lg:p-8 hover:border-red-200 hover:shadow-lg transition-all duration-200 mb-4 sm:mb-6"
              >
                <div class="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-6">
                  <div class="flex items-center space-x-4 sm:space-x-6 flex-1 min-w-0">
                    <div class="flex-shrink-0">
                      <div class="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg" :class="getFileTypeColor(file.original_name, file.mime_type)">
                        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path v-html="getFileTypeIcon(file.original_name, file.mime_type)"></path>
                        </svg>
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center space-x-4 mb-3">
                        <h5 class="text-lg font-bold text-gray-900 truncate">{{ file.original_name }}</h5>
                        <span class="inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold bg-gray-100 text-gray-700">
                          {{ getFileTypeLabel(file.original_name, file.mime_type) }}
                        </span>
                        <span 
                          class="inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold"
                          :class="file.is_public ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'"
                        >
                          {{ file.is_public ? 'Public' : 'Private' }}
                        </span>
                      </div>
                      <div class="flex items-center space-x-6 text-base text-gray-500">
                        <span class="flex items-center space-x-2">
                          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"></path>
                          </svg>
                          <span class="font-medium">{{ formatFileSize(file.file_size) }}</span>
                        </span>
                        <span class="flex items-center space-x-2">
                          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                          </svg>
                          <span class="font-medium">{{ file.uploaded_by === userEmail ? 'You' : file.uploaded_by.split('@')[0] }}</span>
                        </span>
                        <span class="flex items-center space-x-2">
                          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          <span class="font-medium">{{ formatDate(file.uploaded_at) }}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- File Actions -->
                  <div class="flex items-center space-x-2">
                    <!-- Quick Download Button -->
                    <button
                      @click="downloadFile(file)"
                      class="inline-flex items-center justify-center px-3 py-2 text-xs sm:text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all duration-200"
                      title="Download file"
                    >
                      <svg class="w-8 h-8 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                      <span class="hidden md:inline ml-2">Download</span>
                    </button>

                    <!-- Actions Dropdown -->
                    <div class="relative" @click.stop>
                      <button
                        @click="toggleFileActions(file.id)"
                        class="inline-flex items-center justify-center px-3 py-2 text-xs sm:text-sm font-semibold text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-all duration-200"
                        :class="{ 'bg-gray-100': showFileActions === file.id }"
                        title="More actions"
                      >
                        <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path>
                        </svg>
                        <span class="hidden md:inline ml-2">Actions</span>
                      </button>

                      <!-- Dropdown Menu -->
                      <div 
                        v-if="showFileActions === file.id"
                        class="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 z-50"
                        @click.stop
                      >
                        <div class="py-2">
                          <!-- Share -->
                          <button
                            @click="showShareDialog(file); showFileActions = null"
                            class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                          >
                            <svg class="w-8 h-8 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"></path>
                            </svg>
                            Share Link
                          </button>

                          <!-- Public/Private Toggle -->
                          <button
                            v-if="canModifyFile(file)"
                            @click="toggleFilePublic(file); showFileActions = null"
                            class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                          >
                            <svg class="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                 :class="file.is_public ? 'text-yellow-500' : 'text-green-500'">
                              <path v-if="file.is_public" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"></path>
                            </svg>
                            {{ file.is_public ? 'Make Private' : 'Make Public' }}
                          </button>

                          <!-- Divider -->
                          <div v-if="canModifyFile(file)" class="border-t border-gray-100 my-1"></div>

                          <!-- Delete -->
                          <button
                            v-if="canModifyFile(file)"
                            @click="confirmDeleteFile(file); showFileActions = null"
                            class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                          >
                            <svg class="w-8 h-8 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                            Delete File
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Empty Files State -->
          <div v-else-if="!folder.files || folder.files.length === 0" class="mt-8 text-center py-8">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <p class="mt-2 text-sm text-gray-500">No files uploaded yet. Drag and drop or click to upload your first file.</p>
          </div>
        </div>

        <!-- Subfolders Section -->
        <div v-if="folder.subfolders && folder.subfolders.length > 0" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900">Subfolders</h3>
            <button
              v-if="canCreateSubfolder"
              @click="showCreateSubfolderModal = true"
              class="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center space-x-2 shadow-md hover:shadow-lg transition-all duration-200"
            >
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              <span>Create Subfolder</span>
            </button>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="subfolder in folder.subfolders"
              :key="subfolder.id"
              class="group bg-gray-50 border border-gray-200 rounded-xl p-4 hover:border-red-200 hover:shadow-md transition-all duration-200 cursor-pointer"
              @click="navigateToSubfolder(subfolder.id)"
            >
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h4 class="text-sm font-semibold text-gray-900 truncate group-hover:text-red-600 transition-colors">
                    {{ subfolder.name }}
                  </h4>
                  <p class="text-xs text-gray-500 mt-1">
                    {{ formatDate(subfolder.created_at) }}
                  </p>
                </div>
                <svg class="w-8 h-8 text-gray-400 group-hover:text-red-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- User Access Management -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <div class="flex justify-between items-center mb-6">
              <h3 class="text-lg leading-6 font-medium text-gray-900">User Access</h3>
              <button
                v-if="canManageAccess"
                @click="showAddUserModal = true"
                class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center"
              >
                <svg class="w-8 h-8 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
                Add User
              </button>
            </div>

            <!-- Users List -->
            <div v-if="folder.users && folder.users.length > 0" class="space-y-3">
              <div
                v-for="user in folder.users"
                :key="user.user_email"
                class="flex items-center justify-between p-3 border border-gray-200 rounded-md"
              >
                <div class="flex items-center">
                  <div class="flex-shrink-0">
                    <svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm font-medium text-gray-900">{{ user.user_email }}</p>
                    <p class="text-sm text-gray-500">Added by {{ user.granted_by }} on {{ formatDate(user.created_at) }}</p>
                  </div>
                </div>
                <div class="flex items-center space-x-3">
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="getAccessLevelClass(user.access_level)"
                  >
                    {{ user.access_level }}
                  </span>
                  <div v-if="canManageAccess" class="flex items-center space-x-2">
                    <button
                      @click="editUser(user.user_email, user.access_level)"
                      class="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      @click="removeUser(user.user_email)"
                      class="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="text-center py-6">
              <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
              </svg>
              <h3 class="mt-2 text-sm font-medium text-gray-900">No users with access</h3>
              <p class="mt-1 text-sm text-gray-500">Only the folder owner has access.</p>
            </div>
          </div>
        </div>

        <!-- Danger Zone -->
        <div v-if="canManageAccess" class="bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">Danger Zone</h3>
            <div class="border border-red-200 rounded-md p-4">
              <div class="flex">
                <div class="flex-shrink-0">
                  <svg class="h-8 w-8 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div class="ml-3">
                  <h3 class="text-sm font-medium text-red-800">Delete this folder</h3>
                  <div class="mt-2 text-sm text-red-700">
                    <p>Once you delete a folder, there is no going back. Please be certain.</p>
                  </div>
                  <div class="mt-4">
                    <button
                      @click="confirmDelete"
                      class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      Delete Folder
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

          <!-- Add User Modal -->
      <div v-if="showAddUserModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mx-auto">
          <div class="p-10">
            <div class="flex items-center mb-10">
              <div class="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-3xl font-bold text-gray-900">Add User Access</h3>
                <p class="text-gray-600 text-lg mt-2">Grant access to this folder</p>
              </div>
            </div>
            <form @submit.prevent="addUser" class="space-y-8">
              <div>
                <label for="user-email" class="block text-lg font-medium text-gray-700 mb-4">
                  User Email *
                </label>
                <input
                  id="user-email"
                  type="email"
                  required
                  v-model="newUserAccess.email"
                  class="w-full px-6 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-lg"
                  placeholder="Enter user email"
                />
              </div>
              <div>
                <label for="access-level" class="block text-lg font-medium text-gray-700 mb-4">
                  Access Level
                </label>
                <select
                  id="access-level"
                  v-model="newUserAccess.accessLevel"
                  class="w-full px-6 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-lg"
                >
                  <option value="read">Read Only</option>
                  <option value="write">Read & Write</option>
                  <option value="admin">Full Admin</option>
                </select>
              </div>
              <div class="flex justify-end space-x-6 pt-8">
                <button
                  type="button"
                  @click="cancelAddUser"
                  class="px-5 py-2 border border-gray-300 rounded-xl text-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="addingUser"
                  class="px-5 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:opacity-50 text-white rounded-xl text-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {{ addingUser ? 'Adding...' : 'Add User' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Edit Access Modal -->
      <div v-if="showEditAccessModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mx-auto">
          <div class="p-10">
            <div class="flex items-center mb-10">
              <div class="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-3xl font-bold text-gray-900">Edit User Access</h3>
                <p class="text-gray-600 text-lg mt-2">Change access level for {{ editUserAccess.email }}</p>
              </div>
            </div>
            <form @submit.prevent="updateUserAccess" class="space-y-8">
              <div>
                <label for="edit-access-level" class="block text-lg font-medium text-gray-700 mb-4">
                  Access Level
                </label>
                <select
                  id="edit-access-level"
                  v-model="editUserAccess.accessLevel"
                  class="w-full px-6 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-lg"
                >
                  <option value="read">Read Only</option>
                  <option value="write">Read & Write</option>
                  <option value="admin">Full Admin</option>
                </select>
              </div>
              <div class="flex justify-end space-x-6 pt-8">
                <button
                  type="button"
                  @click="cancelEditAccess"
                  class="px-5 py-2 border border-gray-300 rounded-xl text-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="updatingAccess"
                  class="px-5 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:opacity-50 text-white rounded-xl text-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {{ updatingAccess ? 'Updating...' : 'Update Access' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

    <!-- Create Subfolder Modal -->
    <div v-if="showCreateSubfolderModal" class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div class="relative bg-white rounded-3xl shadow-xl w-full max-w-3xl">
        <div class="p-10">
          <div class="flex items-center mb-10">
            <div class="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
              </svg>
            </div>
            <div>
              <h3 class="text-3xl font-bold text-gray-900">Create Subfolder</h3>
              <p class="text-gray-600 text-lg mt-2">Create a new subfolder within this directory</p>
            </div>
          </div>
          
          <form @submit.prevent="createSubfolder" class="space-y-8">
            <div>
              <label for="subfolder-name" class="block text-lg font-medium text-gray-700 mb-4">
                Subfolder Name *
              </label>
              <input
                id="subfolder-name"
                type="text"
                required
                v-model="newSubfolder.name"
                class="w-full px-6 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all text-lg"
                placeholder="Enter subfolder name"
              />
            </div>
            
            <div>
              <label for="subfolder-description" class="block text-lg font-medium text-gray-700 mb-4">
                Description
              </label>
              <textarea
                id="subfolder-description"
                v-model="newSubfolder.description"
                rows="4"
                class="w-full px-6 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none text-lg"
                placeholder="Enter subfolder description (optional)"
              ></textarea>
            </div>
            
            <div class="flex justify-end space-x-6 pt-8">
              <button
                type="button"
                @click="cancelCreateSubfolder"
                class="px-5 py-2 border border-gray-300 rounded-xl text-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="creatingSubfolder"
                class="px-5 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-lg font-semibold transition-all duration-200 flex items-center space-x-2"
              >
                <svg v-if="creatingSubfolder" class="animate-spin w-8 h-8" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{{ creatingSubfolder ? 'Creating...' : 'Create Subfolder' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Share File Dialog -->
    <div v-if="showShareFileModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-[500px] mx-auto">
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <div class="flex items-center space-x-3">
            <div class="w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900">Share File</h3>
          </div>
          <button @click="closeShareDialog" class="text-gray-400 hover:text-gray-600 p-1">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div class="p-6 space-y-4">
          <div v-if="selectedFile">
            <h4 class="font-semibold text-gray-900 mb-3">{{ selectedFile.original_name }}</h4>
            
            <!-- Public File -->
            <div v-if="selectedFile.is_public" class="space-y-4">
              <div class="flex items-center p-3 bg-green-50 rounded-lg">
                <svg class="w-8 h-8 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"></path>
                </svg>
                <span class="text-green-700 font-medium">This file is public</span>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Share Link:</label>
                <div class="flex items-center space-x-2">
                  <input 
                    type="text" 
                    :value="getShareDownloadUrl(selectedFile.id)"
                    readonly
                    class="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                  />
                  <button 
                    @click="copyToClipboard(getShareDownloadUrl(selectedFile.id))"
                    class="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Private File -->
            <div v-else class="space-y-4">
              <div class="flex items-center p-3 bg-yellow-50 rounded-lg">
                <svg class="w-8 h-8 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
                <span class="text-yellow-700 font-medium">This file is password-protected</span>
              </div>
              
              <div v-if="selectedFile.access_password">
                <label class="block text-sm font-medium text-gray-700 mb-2">Password:</label>
                <div class="flex items-center space-x-2">
                  <input 
                    type="text" 
                    :value="selectedFile.access_password"
                    readonly
                    class="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                  />
                  <button 
                    @click="copyToClipboard(selectedFile.access_password)"
                    class="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <!-- If no password exists, show generate button -->
              <div v-else-if="!selectedFile.access_password && canModifyFile(selectedFile)" class="space-y-4">
                <div class="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p class="text-red-700 text-sm">This file doesn't have a password yet.</p>
                </div>
                <button 
                  @click="generateNewPassword(selectedFile.id)"
                  :disabled="generatingPassword"
                  class="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                >
                  <svg v-if="generatingPassword" class="animate-spin w-8 h-8 mr-2 inline" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{{ generatingPassword ? 'Generating...' : 'Generate Password' }}</span>
                </button>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Share Link:</label>
                <div class="flex items-center space-x-2">
                  <input 
                    type="text" 
                    :value="getShareDownloadUrl(selectedFile.id, selectedFile.access_password)"
                    readonly
                    class="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                  />
                  <button 
                    @click="copyToClipboard(getShareDownloadUrl(selectedFile.id, selectedFile.access_password))"
                    class="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                  >
                    Copy
                  </button>
                </div>
                <p class="text-xs text-gray-500 mt-1">
                  <span v-if="selectedFile.access_password">Link includes password - file will download automatically</span>
                  <span v-else>Recipients will be prompted for the password on the download page</span>
                </p>
              </div>
              
              <div v-if="canModifyFile(selectedFile)" class="pt-2 border-t border-gray-200">
                <button 
                  @click="generateNewPassword(selectedFile.id)"
                  :disabled="generatingPassword"
                  class="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium disabled:opacity-50"
                >
                  <svg v-if="generatingPassword" class="animate-spin w-8 h-8 mr-2 inline" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{{ generatingPassword ? 'Generating...' : 'Generate New Password' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Remove User Confirmation Modal -->
    <div v-if="showRemoveUserModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mx-auto">
        <div class="p-10">
          <div class="flex items-center mb-10">
            <div class="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mr-6">
              <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <div>
              <h3 class="text-3xl font-semibold text-gray-900">Remove User Access</h3>
              <p class="text-lg text-gray-600">This action cannot be undone</p>
            </div>
          </div>
          
          <div class="bg-red-50 border border-red-200 rounded-2xl p-8 mb-10">
            <p class="text-red-800 text-lg font-medium mb-3">⚠️ Confirm User Removal</p>
            <p class="text-red-700 text-lg">
              Are you sure you want to remove access for <strong>{{ userToRemove }}</strong>? 
              They will no longer be able to view or modify this folder.
            </p>
          </div>
          
          <div class="flex justify-end space-x-6">
            <button
              @click="cancelRemoveUser"
              class="px-5 py-2 border border-gray-300 rounded-xl text-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="confirmRemoveUser"
              class="px-5 py-2 bg-red-600 text-white rounded-xl text-lg font-medium hover:bg-red-700 transition-colors"
            >
              Remove Access
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Folder Confirmation Modal -->
    <div v-if="showDeleteFolderModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-3xl shadow-2xl w-full max-w-3xl mx-auto">
        <div class="p-10">
          <div class="flex items-center mb-10">
            <div class="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mr-6">
              <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </div>
            <div>
              <h3 class="text-3xl font-semibold text-gray-900">Delete Folder</h3>
              <p class="text-lg text-gray-600">This action cannot be undone</p>
            </div>
          </div>
          
          <div class="bg-red-50 border border-red-200 rounded-2xl p-8 mb-10">
            <p class="text-red-800 font-bold text-xl mb-6">⚠️ Warning: This will permanently delete:</p>
            <ul class="text-red-700 text-lg space-y-3 ml-8">
              <li>• The folder "{{ folder.name }}"</li>
              <li>• All files within this folder</li>
              <li>• All subfolders and their contents</li>
              <li>• All user access permissions</li>
            </ul>
          </div>
          
          <p class="text-gray-700 text-lg mb-10">
            Are you absolutely sure you want to delete this folder? This action cannot be undone.
          </p>
          
          <div class="flex justify-end space-x-6">
            <button
              @click="cancelDeleteFolder"
              class="px-5 py-2 border border-gray-300 rounded-xl text-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="deleteFolderConfirmed"
              class="px-5 py-2 bg-red-600 text-white rounded-xl text-lg font-medium hover:bg-red-700 transition-colors"
            >
              Delete Forever
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete File Confirmation Modal -->
    <div v-if="showDeleteFileModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mx-auto">
        <div class="p-8">
          <div class="flex items-center mb-6">
            <div class="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mr-4">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </div>
            <div>
              <h3 class="text-2xl font-semibold text-gray-900">Delete File</h3>
              <p class="text-gray-600">This action cannot be undone</p>
            </div>
          </div>
          
          <div class="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
            <p class="text-red-800 font-medium text-lg mb-3">⚠️ You are about to permanently delete:</p>
            <div class="text-red-700 bg-white rounded-lg p-4 border border-red-200">
              <div class="flex items-center">
                <div class="w-8 h-8 rounded-lg flex items-center justify-center mr-3"
                     :style="{ backgroundColor: fileToDelete ? getFileTypeColor(fileToDelete.original_name, fileToDelete.mime_type) : '#d1d5db' }">
                  <span class="text-white text-xs font-bold" v-if="fileToDelete">
                    {{ getFileTypeLabel(fileToDelete.original_name, fileToDelete.mime_type) }}
                  </span>
                </div>
                <div>
                  <p class="font-medium" v-if="fileToDelete">{{ fileToDelete.original_name }}</p>
                  <p class="text-sm text-red-600" v-if="fileToDelete">{{ formatFileSize(fileToDelete.file_size) }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <p class="text-gray-700 mb-8">
            Are you sure you want to delete this file? This action cannot be undone and the file will be permanently removed from storage.
          </p>
          
          <div class="flex justify-end space-x-4">
            <button
              @click="cancelDeleteFile"
              class="px-6 py-3 border border-gray-300 rounded-xl text-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="deleteFile"
              class="px-6 py-3 bg-red-600 text-white rounded-xl text-lg font-medium hover:bg-red-700 transition-colors"
            >
              Delete File
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Notification Modal -->
    <NotificationModal
      :show="notification.show"
      :type="notification.type"
      :title="notification.title"
      :message="notification.message"
      :confirmText="notification.confirmText"
      :cancelText="notification.cancelText"
      :showCancel="notification.showCancel"
      @confirm="handleNotificationConfirm"
      @cancel="handleNotificationCancel"
      @close="hideNotification"
    />
  </AdminLayout>
</template>

<script>
import axios from 'axios'
import AdminLayout from '@/components/AdminLayout.vue'
import NotificationModal from '@/components/NotificationModal.vue'
import { getFileType, getFileColor, fileIcons } from '@/utils/fileTypes'

export default {
  name: 'FolderDetail',
  components: {
    AdminLayout,
    NotificationModal
  },
  data() {
    return {
      folder: {},
      loading: true,
      userEmail: '',
      showAddUserModal: false,
      addingUser: false,
      dragOver: false,
      uploading: false,
      showCreateSubfolderModal: false,
      creatingSubfolder: false,
      newUserAccess: {
        email: '',
        accessLevel: 'read'
      },
      // Edit access modal
      showEditAccessModal: false,
      updatingAccess: false,
      editUserAccess: {
        email: '',
        accessLevel: 'read'
      },
      newSubfolder: {
        name: '',
        description: ''
      },
      // Remove user modal
      showRemoveUserModal: false,
      userToRemove: null,
      // Delete folder modal
      showDeleteFolderModal: false,
      // Delete file modal
      showDeleteFileModal: false,
      fileToDelete: null,
      showShareFileModal: false,
      selectedFile: null,
      generatingPassword: false,
      showFileActions: null, // Track which file's dropdown is open
      // Search and filtering
      searchQuery: '',
      selectedFileType: '',
      notification: {
        show: false,
        type: 'info',
        title: '',
        message: '',
        confirmText: 'OK',
        cancelText: 'Cancel',
        showCancel: false,
        onConfirm: null,
        onCancel: null
      }
    }
  },
  computed: {
    shopSettings() {
      return this.$root.shopSettings || {};
    },
    apiUrl() {
      return this.shopSettings?.apiUrl || "https://selectair-vakanties.uptodatesites.com";
    },
    canManageAccess() {
      // Owner can always manage access
      if (this.folder.created_by === this.userEmail) return true
      
      // Admin level users can also manage access
      return this.folder.userAccessLevel === 'admin'
    },
    canWriteFiles() {
      // Owner can always write
      if (this.folder.created_by === this.userEmail) return true
      
      // Check user's access level
      const accessLevel = this.folder.userAccessLevel
      return accessLevel === 'write' || accessLevel === 'admin'
    },
    canCreateSubfolder() {
      // Only owner and admin users can create subfolders
      if (this.folder.created_by === this.userEmail) return true
      
      // Only admin level users can create subfolders (not write level)
      return this.folder.userAccessLevel === 'admin'
    },
    filteredFiles() {
      if (!this.folder.files) return []
      
      let files = [...this.folder.files]
      
      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim()
        files = files.filter(file => 
          file.original_name.toLowerCase().includes(query) ||
          file.uploaded_by.toLowerCase().includes(query) ||
          this.getFileTypeLabel(file.original_name, file.mime_type).toLowerCase().includes(query)
        )
      }
      
      // Apply file type filter
      if (this.selectedFileType) {
        files = files.filter(file => {
          const fileInfo = getFileType(file.original_name, file.mime_type)
          return fileInfo.type === this.selectedFileType
        })
      }
      
      return files
    }
  },
  async mounted() {
    await this.checkAuth()
    await this.loadFolder()
    
    // Close dropdowns when clicking outside
    document.addEventListener('click', () => {
      this.showFileActions = null
    })
  },
  beforeDestroy() {
    // Clean up event listeners
    document.removeEventListener('click', () => {
      this.showFileActions = null
    })
  },
  methods: {
    async checkAuth() {
      const token = localStorage.getItem('admin_token')
      
      if (!token) {
        this.$router.push('/admin/login')
        return
      }

      try {
        const response = await axios.get(`${this.apiUrl}/api/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        if (response.data.success) {
          this.userEmail = response.data.user.email
        } else {
          this.logout()
        }
      } catch (error) {
        this.logout()
      }
    },
    async loadFolder() {
      this.loading = true
      try {
        const token = localStorage.getItem('admin_token')
        const folderId = this.$route.params.id
        
        const response = await axios.get(`${this.apiUrl}/api/admin/folders/${folderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        if (response.data.success) {
          this.folder = response.data.data
        }
      } catch (error) {
        console.error('Error loading folder:', error)
        if (error.response?.status === 404) {
          this.$router.push('/admin/dashboard')
        }
      } finally {
        this.loading = false
      }
    },
    async addUser() {
      if (!this.newUserAccess.email.trim()) return
      
      // Check for duplicate user
      const userExists = this.folder.accessList?.some(user => 
        user.user_email?.toLowerCase() === this.newUserAccess.email.toLowerCase()
      )
      
      if (userExists) {
        this.showNotification('error', 'Duplicate User', 'This user already has access to this folder.')
        return
      }
      
      this.addingUser = true
      try {
        const token = localStorage.getItem('admin_token')
        const folderId = this.$route.params.id
        
        await axios.post(`${this.apiUrl}/api/admin/folders/${folderId}/access`, {
          userEmail: this.newUserAccess.email,
          accessLevel: this.newUserAccess.accessLevel
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        await this.loadFolder()
        this.cancelAddUser()
        this.showNotification('success', 'User Added', 'User access has been successfully granted.')
      } catch (error) {
        console.error('Error adding user:', error)
        this.showNotification('error', 'Error Adding User', error.response?.data?.message || 'Unknown error')
      } finally {
        this.addingUser = false
      }
    },
    async removeUser(userEmail) {
      this.userToRemove = userEmail
      this.showRemoveUserModal = true
    },
    async confirmRemoveUser() {
      try {
        const token = localStorage.getItem('admin_token')
        const folderId = this.$route.params.id
        
        await axios.delete(`${this.apiUrl}/api/admin/folders/${folderId}/access/${encodeURIComponent(this.userToRemove)}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        await this.loadFolder()
        this.showRemoveUserModal = false
        this.showNotification('success', 'User Removed', 'User access has been successfully removed.')
      } catch (error) {
        console.error('Error removing user:', error)
        this.showNotification('error', 'Error Removing User', error.response?.data?.message || 'Unknown error')
      }
    },
    cancelRemoveUser() {
      this.showRemoveUserModal = false
      this.userToRemove = null
    },
    editUser(userEmail, currentAccessLevel) {
      this.editUserAccess.email = userEmail
      this.editUserAccess.accessLevel = currentAccessLevel
      this.showEditAccessModal = true
    },
    async updateUserAccess() {
      this.updatingAccess = true
      try {
        const token = localStorage.getItem('admin_token')
        const folderId = this.$route.params.id
        
        await axios.put(`${this.apiUrl}/api/admin/folders/${folderId}/access/${encodeURIComponent(this.editUserAccess.email)}`, {
          accessLevel: this.editUserAccess.accessLevel
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        await this.loadFolder()
        this.cancelEditAccess()
        this.showNotification('success', 'Access Updated', 'User access level has been successfully updated.')
      } catch (error) {
        console.error('Error updating user access:', error)
        this.showNotification('error', 'Error Updating Access', error.response?.data?.message || 'Unknown error')
      } finally {
        this.updatingAccess = false
      }
    },
    cancelEditAccess() {
      this.showEditAccessModal = false
      this.editUserAccess = { email: '', accessLevel: 'read' }
    },
    confirmDelete() {
      this.showDeleteFolderModal = true
    },
    async deleteFolderConfirmed() {
      try {
        const token = localStorage.getItem('admin_token')
        const folderId = this.$route.params.id
        
        await axios.delete(`${this.apiUrl}/api/admin/folders/${folderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        this.showDeleteFolderModal = false
        this.showNotification('success', 'Folder Deleted', 'Folder has been successfully deleted.')
        
        // Navigate after a short delay to show the notification
        setTimeout(() => {
          this.$router.push('/admin/dashboard')
        }, 1000)
      } catch (error) {
        console.error('Error deleting folder:', error)
        this.showNotification('error', 'Error Deleting Folder', error.response?.data?.message || 'Unknown error')
      }
    },
    cancelDeleteFolder() {
      this.showDeleteFolderModal = false
    },
    cancelAddUser() {
      this.showAddUserModal = false
      this.newUserAccess = { email: '', accessLevel: 'read' }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      })
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },
    getAccessLevelClass(level) {
      const classes = {
        read: 'bg-green-100 text-green-800',
        write: 'bg-blue-100 text-blue-800',
        admin: 'bg-purple-100 text-purple-800'
      }
      return classes[level] || 'bg-gray-100 text-gray-800'
    },
    triggerFileUpload() {
      this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      this.uploadFiles(files)
    },
    handleDrop(event) {
      event.preventDefault()
      this.dragOver = false
      const files = Array.from(event.dataTransfer.files)
      this.uploadFiles(files)
    },
    async uploadFiles(files) {
      if (files.length === 0) return
      
      this.uploading = true
      try {
        const token = localStorage.getItem('admin_token')
        const folderId = this.$route.params.id
        
        const formData = new FormData()
        files.forEach(file => {
          formData.append('files', file)
        })
        
        await axios.post(`${this.apiUrl}/api/admin/folders/${folderId}/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        })
        
        // Reload folder data to show new files
        await this.loadFolder()
        
        // Reset file input
        this.$refs.fileInput.value = ''
        
        // Show success toast
        const fileCount = files.length
        const message = fileCount === 1 ? `1 file uploaded successfully` : `${fileCount} files uploaded successfully`
        this.$root.$toast.success(message, 'Upload Complete')
        
      } catch (error) {
        console.error('Error uploading files:', error)
        this.$root.$toast.error('Failed to upload files. Please try again.', 'Upload Error')
      } finally {
        this.uploading = false
      }
    },
    async downloadFile(file) {
      try {
        // For public files, use direct download approach
        if (file.is_public) {
          const downloadUrl = `${this.apiUrl}/api/admin/files/${file.id}/download`
          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = file.original_name
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          link.remove()
          
          this.$root.$toast.success('Your file download has begun successfully.', 'Download Started')
          return
        }
        
        // For private files, use authenticated blob method
        const token = localStorage.getItem('admin_token')
        const response = await axios.get(`${this.apiUrl}/api/admin/files/${file.id}/download`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        })
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', file.original_name)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        
        this.$root.$toast.success('Your file download has begun successfully.', 'Download Started')
        
      } catch (error) {
        console.error('Error downloading file:', error)
        this.$root.$toast.error('Failed to download file. Please try again.', 'Download Error')
      }
    },
    confirmDeleteFile(file) {
      this.fileToDelete = file
      this.showDeleteFileModal = true
    },
    async deleteFile() {
      try {
        const token = localStorage.getItem('admin_token')
        await axios.delete(`${this.apiUrl}/api/admin/files/${this.fileToDelete.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        await this.loadFolder()
        this.showDeleteFileModal = false
        this.$root.$toast.success('File deleted successfully.', 'File Deleted')
        
      } catch (error) {
        console.error('Error deleting file:', error)
        this.$root.$toast.error('Failed to delete file. Please try again.', 'Delete Failed')
      }
    },
    cancelDeleteFile() {
      this.showDeleteFileModal = false
      this.fileToDelete = null
    },
    toggleFileActions(fileId) {
      this.showFileActions = this.showFileActions === fileId ? null : fileId
    },
    canDeleteFile(file) {
      return file.uploaded_by === this.userEmail || this.folder.created_by === this.userEmail
    },
    canModifyFile(file) {
      // Owner can always modify
      if (this.folder.created_by === this.userEmail) return true
      
      // File uploader can modify their own files if they have write access
      if (file.uploaded_by === this.userEmail && this.canWriteFiles) return true
      
      // Users with admin access can modify any file
      const accessLevel = this.folder.userAccessLevel
      return accessLevel === 'admin'
    },
    getFileTypeColor(fileName, mimeType) {
      return getFileColor(fileName, mimeType)
    },
    getFileTypeIcon(fileName, mimeType) {
      const fileInfo = getFileType(fileName, mimeType)
      return fileIcons[fileInfo.icon] || fileIcons['document']
    },
    getFileTypeLabel(fileName, mimeType) {
      const fileInfo = getFileType(fileName, mimeType)
      return fileInfo.type.toUpperCase()
    },
    getDirectDownloadUrl(fileId, isPublic) {
      if (isPublic) {
        return `${this.apiUrl}/api/admin/files/${fileId}/download`
      } else {
        const token = localStorage.getItem('admin_token')
        return `${this.apiUrl}/api/admin/files/${fileId}/download?token=${encodeURIComponent(token)}`
      }
    },
    async toggleFilePublic(file) {
      try {
        const token = localStorage.getItem('admin_token')
        const newStatus = !file.is_public
        
        await axios.put(`${this.apiUrl}/api/admin/files/${file.id}/public`, {
          isPublic: newStatus
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        // Update the file in the local array
        file.is_public = newStatus
        
      } catch (error) {
        console.error('Error toggling file public status:', error)
        this.$root.$toast.error('Failed to change file visibility. Please try again.', 'Update Failed')
      }
    },
    async createSubfolder() {
      if (!this.newSubfolder.name.trim()) return
      
      this.creatingSubfolder = true
      try {
        const token = localStorage.getItem('admin_token')
        const folderId = this.$route.params.id
        
        await axios.post(`${this.apiUrl}/api/admin/folders`, {
          name: this.newSubfolder.name,
          description: this.newSubfolder.description,
          parent_id: folderId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        await this.loadFolder() // Reload to show new subfolder
        this.cancelCreateSubfolder()
        
      } catch (error) {
        console.error('Error creating subfolder:', error)
        alert('Error creating subfolder: ' + (error.response?.data?.message || 'Unknown error'))
      } finally {
        this.creatingSubfolder = false
      }
    },
    cancelCreateSubfolder() {
      this.showCreateSubfolderModal = false
      this.newSubfolder = { name: '', description: '' }
    },
    navigateToSubfolder(subfolderid) {
      const currentId = this.$route.params.id
      console.log('Navigating from folder', currentId, 'to subfolder', subfolderid)
      
      if (currentId !== subfolderid.toString()) {
        // Clear current data before navigation
        this.folder = { files: [], users: [], subfolders: [] }
        this.loading = true
        
        this.$router.push(`/admin/folders/${subfolderid}`).then(() => {
          // Force reload after navigation
          this.$nextTick(() => {
            this.loadFolder()
          })
        }).catch(err => {
          console.error('Navigation error:', err)
          // Force navigation if there's an issue
          window.location.href = `#/admin/folders/${subfolderid}`
        })
      }
    },
    
    // File management methods
    async toggleFilePublic(file) {
      try {
        const token = localStorage.getItem('admin_token')
        const response = await axios.put(`${this.apiUrl}/api/admin/files/${file.id}/public`, {
          isPublic: !file.is_public
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        if (response.data.success) {
          // Update the file in the list
          file.is_public = response.data.data.isPublic
          
          // If password was generated for private file, store it
          if (response.data.data.password) {
            file.access_password = response.data.data.password
            this.showNotification('success', 'File Made Private', `Password generated: ${response.data.data.password}`)
          }
        }
      } catch (error) {
        console.error('Error toggling file visibility:', error)
        this.showNotification('error', 'Error', 'Failed to update file visibility: ' + (error.response?.data?.message || 'Unknown error'))
      }
    },
    
    // Navigation methods
    goBackToPreviousFolder() {
      if (this.folder.hierarchy && this.folder.hierarchy.length > 1) {
        // Navigate to parent folder
        const parentFolder = this.folder.hierarchy[this.folder.hierarchy.length - 2]
        this.navigateToFolder(parentFolder.id)
      } else {
        // Go back to dashboard
        this.$router.push('/admin/dashboard').catch(err => {
          // Ignore navigation duplication errors
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err)
          }
        })
      }
    },
    
    navigateToFolder(folderId) {
      const currentId = this.$route.params.id
      if (currentId !== folderId.toString()) {
        this.$router.push(`/admin/folders/${folderId}`).catch(err => {
          // Ignore navigation duplication errors
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err)
          }
        })
      }
    },
    
    // Share dialog methods
    showShareDialog(file) {
      this.selectedFile = file
      this.showShareFileModal = true
    },
    
    closeShareDialog() {
      this.showShareFileModal = false
      this.selectedFile = null
    },
    
    getPublicDownloadUrl(file) {
      // For public files, we can link directly to S3 (faster)
      if (file.s3_url) {
        // Use direct S3 URL with download disposition
        const s3Key = file.s3_key || file.file_path;
        if (s3Key) {
          const encodedFilename = encodeURIComponent(file.original_name);
          return `https://selectair.s3.eu-west-3.amazonaws.com/${s3Key}?response-content-disposition=attachment%3B%20filename%3D%22${encodedFilename}%22`;
        }
      }
      // Fallback to our API
      return `${this.apiUrl}/api/admin/files/${file.id}/download`;
    },
    
    getShareDownloadUrl(fileId, password = null, options = {}) {
      const baseUrl = `${window.location.protocol}//${window.location.host}/#/download/${fileId}`
      const params = new URLSearchParams()
      
      // Add password if provided
      if (password) {
        params.append('password', password)
      }
      
      // Add additional options
      if (options.admin) {
        params.append('admin', 'true')
      }
      
      if (options.autodownload === false) {
        params.append('autodownload', 'false')
      }
      
      const queryString = params.toString()
      return queryString ? `${baseUrl}?${queryString}` : baseUrl
    },
    
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.$root.$toast.success('Link copied to clipboard successfully.', 'Copied!')
      } catch (err) {
        console.error('Failed to copy: ', err)
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$root.$toast.success('Link copied to clipboard successfully.', 'Copied!')
      }
    },

    showNotification(type, title, message, options = {}) {
      this.notification = {
        show: true,
        type,
        title,
        message,
        confirmText: options.confirmText || 'OK',
        cancelText: options.cancelText || 'Cancel',
        showCancel: options.showCancel || false,
        onConfirm: options.onConfirm || null,
        onCancel: options.onCancel || null
      }
    },

    hideNotification() {
      this.notification.show = false
    },

    handleNotificationConfirm() {
      if (this.notification.onConfirm) {
        this.notification.onConfirm()
      }
      this.hideNotification()
    },

    handleNotificationCancel() {
      if (this.notification.onCancel) {
        this.notification.onCancel()
      }
      this.hideNotification()
    },
    
    async generateNewPassword(fileId) {
      try {
        this.generatingPassword = true
        const token = localStorage.getItem('admin_token')
        
        const response = await axios.post(`${this.apiUrl}/api/admin/files/${fileId}/generate-password`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        if (response.data.success) {
          // Update the selected file with new password
          if (this.selectedFile && this.selectedFile.id === fileId) {
            this.selectedFile.access_password = response.data.data.password
          }
          // Update the file in the folder list as well
          const fileIndex = this.folder.files.findIndex(f => f.id === fileId)
          if (fileIndex >= 0) {
            this.folder.files[fileIndex].access_password = response.data.data.password
          }
          this.$root.$toast.success(`New password: ${response.data.data.password}`, 'Password Generated')
        }
      } catch (error) {
        console.error('Error generating password:', error)
        this.$root.$toast.error('Failed to generate password: ' + (error.response?.data?.message || 'Unknown error'))
      } finally {
        this.generatingPassword = false
      }
    },

    getFileTypeColor(filename, mimeType) {
      return getFileColor(filename, mimeType)
    },

    getFileTypeIcon(filename, mimeType) {
      const fileInfo = getFileType(filename, mimeType)
      return fileIcons[fileInfo.icon] || fileIcons.document
    },

    getFileTypeLabel(filename, mimeType) {
      const fileInfo = getFileType(filename, mimeType)
      return fileInfo.type.toUpperCase()
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString()
    },
    
    // Search and filtering methods
    clearFilters() {
      this.searchQuery = ''
      this.selectedFileType = ''
    },
    
    logout() {
      localStorage.removeItem('admin_token')
      localStorage.removeItem('admin_user')
      localStorage.removeItem('uptodate_token')
      localStorage.removeItem('currentClient')
      this.$router.push('/admin/login')
    }
  }
}
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
